import type { AxiosError } from "axios";
import { checkinApi } from "@wkt/api/checkin/checkinAPI";
import type {
  CurrentWorkdayServiceResponseDTO,
  EmployeeCalendarDTO,
  SimpleDateDTO,
  SimpleDateRangeDTO,
  VacationDataDTO,
  WorkdayDTO,
} from "@wkt/api/checkin/checkinDTO";
import { parseSimpleDateValue, toSimpleDateKey } from "@wkt/composables/useSimpleDate";
import { useSessionCatalogStore } from "@wkt/stores/sessionCatalog";
import { defineStore } from "pinia";

type BridgeStatus = "idle" | "waiting" | "ready" | "error";

interface CheckinState {
  currentWorkday: WorkdayDTO | null;
  selectedWorkday: WorkdayDTO | null;
  selectedDate: SimpleDateDTO | null;
  todayCalendar: EmployeeCalendarDTO | null;
  calendarCache: Record<string, EmployeeCalendarDTO>;
  vacationData: VacationDataDTO | null;
  globalBusy: boolean;
  selectionBusy: boolean;
  bridgeStatus: BridgeStatus;
  hydrating: boolean;
  hydratedAt: string | null;
  lastError: string | null;
}

const BRIDGE_WAIT_INTERVAL_MS = 100;
const BRIDGE_WAIT_MAX_ATTEMPTS = 40;
let inFlightWorkdayRefreshKey: string | null = null;
let inFlightWorkdayRefreshPromise: Promise<boolean> | null = null;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function callApi<T>(
  runner: (
    onSuccess: (data: T) => void,
    onError: (error: AxiosError) => void,
  ) => void,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    runner(resolve, reject);
  });
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }

  return "Unable to refresh check-in data.";
}

const parseCalendarDay = parseSimpleDateValue;

function dateToSimpleDate(date: Date): SimpleDateDTO {
  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
  };
}

function simpleDateToDate(date?: SimpleDateDTO | null): Date | null {
  if (!date?.year || !date?.month || !date?.day) {
    return null;
  }

  return new Date(date.year, date.month - 1, date.day, 12, 0, 0);
}

function getTodaySimpleDate(): SimpleDateDTO {
  return dateToSimpleDate(new Date());
}

function isSameSimpleDate(
  left?: SimpleDateDTO | null,
  right?: SimpleDateDTO | null,
): boolean {
  const leftKey = toSimpleDateKey(left);
  return leftKey != null && leftKey === toSimpleDateKey(right);
}

function isSameWorkdayIdentity(
  left?: WorkdayDTO | null,
  right?: WorkdayDTO | null,
): boolean {
  if (!left || !right) {
    return false;
  }

  if (left.id != null && right.id != null) {
    return left.id === right.id;
  }

  return isSameSimpleDate(left.workdayDate, right.workdayDate);
}

function getWeekRange(date: SimpleDateDTO): SimpleDateRangeDTO {
  const anchor = simpleDateToDate(date) ?? new Date();
  const mondayOffset = (anchor.getDay() + 6) % 7;
  const fromDate = new Date(anchor);
  const toDate = new Date(anchor);

  fromDate.setDate(anchor.getDate() - mondayOffset);
  toDate.setDate(fromDate.getDate() + 6);

  return {
    fromDate: dateToSimpleDate(fromDate),
    toDate: dateToSimpleDate(toDate),
  };
}

function resolveSelectedDate(
  ...candidates: Array<SimpleDateDTO | null | undefined>
): SimpleDateDTO {
  for (const candidate of candidates) {
    if (candidate?.year && candidate.month && candidate.day) {
      return {
        year: candidate.year,
        month: candidate.month,
        day: candidate.day,
      };
    }
  }

  return getTodaySimpleDate();
}

function shouldSyncCurrentWorkdaySnapshot(
  targetDate: SimpleDateDTO,
  workday: WorkdayDTO | null,
  todayCalendar?: EmployeeCalendarDTO | null,
): boolean {
  if (workday?.closed !== true) {
    return true;
  }

  const todayDate =
    parseCalendarDay(todayCalendar?.calendarDay) ?? getTodaySimpleDate();

  return isSameSimpleDate(targetDate, todayDate);
}

function normalizeCurrentWorkdaySnapshot(
  workday: WorkdayDTO | null,
): WorkdayDTO | null {
  if (!workday?.workdayDate) {
    return workday;
  }

  const workdayDate = simpleDateToDate(workday.workdayDate);
  const todayDate = simpleDateToDate(getTodaySimpleDate());

  if (!workdayDate || !todayDate) {
    return workday;
  }

  return workdayDate.getTime() > todayDate.getTime() ? null : workday;
}

export const useCheckinStore = defineStore("checkin", {
  state: (): CheckinState => ({
    currentWorkday: null,
    selectedWorkday: null,
    selectedDate: null,
    todayCalendar: null,
    calendarCache: {},
    vacationData: null,
    globalBusy: false,
    selectionBusy: false,
    bridgeStatus: "idle",
    hydrating: false,
    hydratedAt: null,
    lastError: null,
  }),

  getters: {
    isReady: (state) => state.bridgeStatus === "ready",
    selectedCalendar: (state): EmployeeCalendarDTO | null => {
      const selectedKey = toSimpleDateKey(state.selectedDate);
      if (!selectedKey) {
        return null;
      }

      return (
        state.calendarCache[selectedKey] ??
        (toSimpleDateKey(state.todayCalendar?.calendarDay) === selectedKey
          ? state.todayCalendar
          : null)
      );
    },
  },

  actions: {
    setCurrentWorkdaySnapshot(workday: WorkdayDTO | null): void {
      const normalizedWorkday = normalizeCurrentWorkdaySnapshot(workday);
      const previousWorkday = this.currentWorkday;
      const previousWorkdayDate = this.currentWorkday?.workdayDate;
      const selectedFollowedPreviousCurrent = isSameWorkdayIdentity(
        this.selectedWorkday,
        previousWorkday,
      );
      const selectedMatchesPreviousCurrent = isSameSimpleDate(
        this.selectedDate,
        previousWorkdayDate,
      );
      const selectedMatchesNextCurrent = isSameSimpleDate(
        this.selectedDate,
        normalizedWorkday?.workdayDate,
      );
      this.currentWorkday = normalizedWorkday;

      const shouldFollowActiveDay =
        !this.selectedDate ||
        isSameSimpleDate(this.selectedDate, previousWorkdayDate);

      if (shouldFollowActiveDay) {
        this.selectedDate = resolveSelectedDate(
          this.selectedDate,
          parseCalendarDay(this.todayCalendar?.calendarDay),
          getTodaySimpleDate(),
          normalizedWorkday?.workdayDate,
        );
      }

      if (selectedMatchesNextCurrent || selectedMatchesPreviousCurrent) {
        this.selectedWorkday = normalizedWorkday;
      } else if (selectedFollowedPreviousCurrent || shouldFollowActiveDay) {
        this.selectedWorkday = null;
      }

      this.bridgeStatus = "ready";
      this.hydratedAt = new Date().toISOString();
      this.lastError = null;
    },

    getCalendarForDate(
      date?: SimpleDateDTO | null,
    ): EmployeeCalendarDTO | null {
      const dateKey = toSimpleDateKey(date);
      if (!dateKey) {
        return null;
      }

      return (
        this.calendarCache[dateKey] ??
        (toSimpleDateKey(this.todayCalendar?.calendarDay) === dateKey
          ? this.todayCalendar
          : null)
      );
    },

    async ensureHydrated(): Promise<boolean> {
      if (this.bridgeStatus === "ready" && !this.hydrating) {
        const hasSnapshot =
          this.currentWorkday != null ||
          this.todayCalendar != null ||
          this.vacationData != null;

        if (hasSnapshot) {
          this.selectedDate = resolveSelectedDate(
            this.selectedDate,
            parseCalendarDay(this.todayCalendar?.calendarDay),
            getTodaySimpleDate(),
            this.currentWorkday?.workdayDate,
          );
          this.selectedWorkday = isSameSimpleDate(
            this.currentWorkday?.workdayDate,
            this.selectedDate,
          )
            ? this.currentWorkday
            : this.selectedWorkday;
          return true;
        }
      }

      if (this.hydrating) {
        for (
          let attempt = 0;
          attempt < BRIDGE_WAIT_MAX_ATTEMPTS;
          attempt += 1
        ) {
          if (!this.hydrating) {
            return this.bridgeStatus === "ready";
          }

          await sleep(BRIDGE_WAIT_INTERVAL_MS);
        }

        return this.bridgeStatus === "ready";
      }

      this.hydrating = true;
      this.bridgeStatus = "waiting";
      this.lastError = null;

      const sessionCatalogStore = useSessionCatalogStore();
      const ready = await sessionCatalogStore.ensureSessionReady();

      if (!ready) {
        this.hydrating = false;
        this.bridgeStatus = "error";
        this.lastError = sessionCatalogStore.lastError;
        return false;
      }

      const targetDate = resolveSelectedDate(
        this.selectedDate,
        parseCalendarDay(this.todayCalendar?.calendarDay),
        getTodaySimpleDate(),
        this.currentWorkday?.workdayDate,
      );

      try {
        const [workdayResponse, calendarDays, vacationData] = await Promise.all([
          callApi<CurrentWorkdayServiceResponseDTO>((onSuccess, onError) => {
            checkinApi.getCurrentWorkday(targetDate, onSuccess, onError);
          }),
          callApi<EmployeeCalendarDTO[]>((onSuccess, onError) => {
            checkinApi.getCalendar(getWeekRange(targetDate), onSuccess, onError);
          }),
          callApi<VacationDataDTO>((onSuccess, onError) => {
            checkinApi.getVacationData(onSuccess, onError);
          }),
        ]);

        const currentWorkday =
          workdayResponse?.rsOK === false && workdayResponse?.workday == null
            ? null
            : normalizeCurrentWorkdaySnapshot(workdayResponse?.workday ?? null);

        this.currentWorkday = currentWorkday;
        this.vacationData = vacationData ?? null;
        this.calendarCache = {};

        for (const calendarDay of calendarDays ?? []) {
          const calendarDayKey = toSimpleDateKey(calendarDay?.calendarDay);
          if (calendarDayKey) {
            this.calendarCache[calendarDayKey] = calendarDay;
          }
        }

        const todayKey = toSimpleDateKey(getTodaySimpleDate());
        this.todayCalendar = todayKey ? this.calendarCache[todayKey] ?? null : null;
        this.selectedDate = resolveSelectedDate(
          targetDate,
          parseCalendarDay(this.todayCalendar?.calendarDay),
          getTodaySimpleDate(),
          currentWorkday?.workdayDate,
        );
        this.selectedWorkday = isSameSimpleDate(
          currentWorkday?.workdayDate,
          this.selectedDate,
        )
          ? currentWorkday
          : null;
        this.bridgeStatus = "ready";
        this.hydratedAt = new Date().toISOString();
        this.lastError = null;
        this.hydrating = false;
        return true;
      } catch (error) {
        this.hydrating = false;
        this.bridgeStatus = "error";
        this.lastError = getErrorMessage(error);
        return false;
      }
    },

    async refreshWorkday(date?: SimpleDateDTO | null): Promise<boolean> {
      const ready = await this.ensureHydrated();
      if (!ready) {
        return false;
      }

      const targetDate = resolveSelectedDate(
        date,
        this.selectedDate,
        parseCalendarDay(this.todayCalendar?.calendarDay),
        getTodaySimpleDate(),
        this.currentWorkday?.workdayDate,
      );
      const targetDateKey = toSimpleDateKey(targetDate) ?? "__today__";

      if (
        inFlightWorkdayRefreshPromise &&
        inFlightWorkdayRefreshKey === targetDateKey
      ) {
        return inFlightWorkdayRefreshPromise;
      }

      const refreshPromise = (async () => {
        const response = await callApi<CurrentWorkdayServiceResponseDTO>(
          (onSuccess, onError) => {
            checkinApi.getCurrentWorkday(targetDate, onSuccess, onError);
          },
        );

        if (response?.rsOK === false && response.workday == null) {
          this.selectedDate = targetDate;
          this.selectedWorkday = null;
          if (
            shouldSyncCurrentWorkdaySnapshot(
              targetDate,
              null,
              this.todayCalendar,
            )
          ) {
            this.setCurrentWorkdaySnapshot(null);
          }
          this.lastError = null;
          return true;
        }

        if (response?.rsOK === false) {
          this.lastError =
            response.message || "Unable to refresh the selected workday.";
          return false;
        }

        const responseWorkday = response?.workday ?? null;
        const selectedWorkday = isSameSimpleDate(
          responseWorkday?.workdayDate,
          targetDate,
        )
          ? responseWorkday
          : null;
        this.selectedDate = targetDate;
        this.selectedWorkday = selectedWorkday;

        if (
          shouldSyncCurrentWorkdaySnapshot(
            targetDate,
            responseWorkday,
            this.todayCalendar,
          )
        ) {
          this.setCurrentWorkdaySnapshot(responseWorkday);
        }

        this.bridgeStatus = "ready";
        this.hydratedAt = new Date().toISOString();
        this.lastError = null;
        return true;
      })().catch((error) => {
        this.lastError = getErrorMessage(error);
        return false;
      });

      inFlightWorkdayRefreshKey = targetDateKey;
      inFlightWorkdayRefreshPromise = refreshPromise;

      try {
        return await refreshPromise;
      } finally {
        if (inFlightWorkdayRefreshPromise === refreshPromise) {
          inFlightWorkdayRefreshKey = null;
          inFlightWorkdayRefreshPromise = null;
        }
      }
    },

    async refreshCalendar(date?: SimpleDateDTO | null): Promise<boolean> {
      const ready = await this.ensureHydrated();
      if (!ready) {
        return false;
      }

      const targetDate = resolveSelectedDate(
        date,
        this.selectedDate,
        parseCalendarDay(this.todayCalendar?.calendarDay),
        getTodaySimpleDate(),
        this.currentWorkday?.workdayDate,
      );

      try {
        const calendarDays = await callApi<EmployeeCalendarDTO[]>(
          (onSuccess, onError) => {
            checkinApi.getCalendar(
              getWeekRange(targetDate),
              onSuccess,
              onError,
            );
          },
        );

        for (const calendarDay of calendarDays) {
          const calendarDayKey = toSimpleDateKey(calendarDay?.calendarDay);
          if (calendarDayKey) {
            this.calendarCache[calendarDayKey] = calendarDay;
          }
        }

        const todayKey = toSimpleDateKey(getTodaySimpleDate());
        if (todayKey) {
          this.todayCalendar =
            this.calendarCache[todayKey] ?? this.todayCalendar;
        }

        this.selectedDate = targetDate;
        this.bridgeStatus = "ready";
        this.hydratedAt = new Date().toISOString();
        this.lastError = null;
        return true;
      } catch (error) {
        this.lastError = getErrorMessage(error);
        return false;
      }
    },

    async refreshTodayCalendar(): Promise<boolean> {
      return this.refreshCalendar(getTodaySimpleDate());
    },

    async refreshVacationData(): Promise<boolean> {
      try {
        this.vacationData = await callApi<VacationDataDTO>(
          (onSuccess, onError) => {
            checkinApi.getVacationData(onSuccess, onError);
          },
        );
        this.lastError = null;
        this.hydratedAt = new Date().toISOString();
        return true;
      } catch (error) {
        this.lastError = getErrorMessage(error);
        return false;
      }
    },

    async selectDate(date?: SimpleDateDTO | null): Promise<boolean> {
      const ready = await this.ensureHydrated();
      if (!ready) {
        return false;
      }

      const targetDate = resolveSelectedDate(
        date,
        this.selectedDate,
        parseCalendarDay(this.todayCalendar?.calendarDay),
        getTodaySimpleDate(),
        this.currentWorkday?.workdayDate,
      );

      this.selectionBusy = true;
      this.selectedDate = targetDate;

      try {
        const calendarOk = await this.refreshCalendar(targetDate);
        const workdayOk = await this.refreshWorkday(targetDate);
        return calendarOk && workdayOk;
      } finally {
        this.selectionBusy = false;
      }
    },

    setGlobalBusy(busy: boolean): void {
      this.globalBusy = busy;
    },
  },
});
